import { documentTypeConfig } from '../config/documentConfigs';
import {
  getPageTarget,
  getEstimatedTotalChars,
  getEstimatedParts,
  needsMultiPart,
  getMaxTokensPerPart,
} from '../config/documentPageTargets';
import {
  generateCacheKey,
  generateFormHash,
  checkCache,
  getCachedDocument,
  saveToCache
} from './cacheService';
import { uploadDocument } from './storageService';
import {
  trackGenerationMetrics,
  estimateTokenCount,
  estimateCost
} from './metricsService';
import { mockAgreements } from '../utils/mockDocumentContent';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const STREAM_TIMEOUT_MS = 660_000;
const STREAM_STALL_MS = 180_000;
const CONTEXT_TAIL_CHARS = 12000;
const MAX_PARTS = 6;
const CONTINUATION_MARKER = '[CONTINUE]';

interface GenerateDocumentOptions {
  documentType: string;
  formData: Record<string, any>;
  tier?: string;
  onProgress?: (progress: GenerationProgress) => void;
  onStreamChunk?: (accumulated: string, chunkLength: number) => void;
}

interface GenerationResult {
  content: string;
  cacheHit: boolean;
  generationTimeMs: number;
  tokenCount?: number;
  costEstimate?: number;
}

export interface GenerationProgress {
  status: 'generating' | 'streaming' | 'complete' | 'failed';
  message?: string;
  streamedChars?: number;
  estimatedTotalChars?: number;
  currentPart?: number;
  totalParts?: number;
}

interface StreamResult {
  content: string;
  stopReason?: string;
}

export const generateDocument = async ({
  documentType,
  formData,
  tier = 'premium',
  onProgress,
  onStreamChunk
}: GenerateDocumentOptions): Promise<GenerationResult> => {
  const startTime = Date.now();
  const config = documentTypeConfig[documentType];

  if (!config) {
    throw new Error(`Unknown document type: ${documentType}`);
  }

  let cacheKey: string | undefined;

  try {
    cacheKey = await generateCacheKey(formData, documentType, 'premium');
    const cachedEntry = await checkCache(cacheKey);

    if (cachedEntry) {
      const cachedContent = await getCachedDocument(cachedEntry);
      const generationTime = Date.now() - startTime;

      try {
        await trackGenerationMetrics({
          document_type: documentType,
          tier,
          generation_time_ms: generationTime,
          cache_hit: true,
        });
      } catch (metricsError) {
        console.warn('Failed to track metrics (table may not exist):', metricsError);
      }

      return {
        content: cachedContent,
        cacheHit: true,
        generationTimeMs: generationTime,
      };
    }
  } catch (cacheError) {
    console.warn('Cache lookup failed (table may not exist), proceeding without cache:', cacheError);
  }

  let content: string;

  try {
    if (needsMultiPart(documentType)) {
      content = await generateMultiPart({
        documentType,
        formData,
        config,
        onProgress,
        onStreamChunk,
      });
    } else {
      content = await generateSinglePart({
        documentType,
        formData,
        config,
        onProgress,
        onStreamChunk,
      });
    }
  } catch (generationError) {
    console.error('Document generation failed, using fallback content:', generationError);
    const fallbackTier = (tier === 'standard' || tier === 'enhanced' || tier === 'premium') ? tier : 'premium';
    const fallbackContent = mockAgreements[fallbackTier as keyof typeof mockAgreements];
    const generationTime = Date.now() - startTime;

    return {
      content: fallbackContent,
      cacheHit: false,
      generationTimeMs: generationTime,
    };
  }

  content = stripContinuationMarkers(content);
  content = ensureDocumentCompleteness(content);

  const tokenCount = estimateTokenCount(content);
  const costEstimate = estimateCost(tokenCount, tier);
  const generationTime = Date.now() - startTime;

  const formDataHash = await generateFormHash(formData, documentType);
  const contentPreview = content.substring(0, 500);

  try {
    const uploadResult = await uploadDocument(
      `temp_${Date.now()}`,
      tier,
      content
    );

    if (cacheKey) {
      await saveToCache(
        cacheKey,
        documentType,
        formDataHash,
        tier,
        uploadResult.path,
        contentPreview,
        'claude'
      );
    }
  } catch (error) {
    console.error('Error caching document:', error);
  }

  try {
    await trackGenerationMetrics({
      document_type: documentType,
      tier,
      generation_time_ms: generationTime,
      cache_hit: false,
      token_count: tokenCount,
      cost_estimate: costEstimate,
      provider: 'claude',
    });
  } catch (metricsError) {
    console.warn('Failed to track metrics (table may not exist):', metricsError);
  }

  return {
    content,
    cacheHit: false,
    generationTimeMs: generationTime,
    tokenCount,
    costEstimate,
  };
};

const buildUserPrompt = (documentType: string, formData: Record<string, any>, isMultiPart: boolean): string => {
  const pageTarget = getPageTarget(documentType);
  const config = documentTypeConfig[documentType];
  const docTitle = config?.formSections?.details?.title || documentType;

  const grouped: Record<string, string[]> = {
    'Parties & Contact': [],
    'Property Details': [],
    'Financial Terms': [],
    'Tenancy Terms': [],
    'Legal & Compliance': [],
    'Other Details': [],
  };

  const fieldLabels: Record<string, string> = {
    landlordName: 'Landlord Full Name',
    landlordEmail: 'Landlord Email',
    landlordAddress: 'Landlord Address',
    tenantName: 'Tenant Full Name',
    tenantEmail: 'Tenant Email',
    tenantCompanyName: 'Tenant Company Name',
    propertyAddress: 'Property Address',
    propertyType: 'Property Type',
    startDate: 'Tenancy Start Date',
    rentAmount: 'Monthly Rent (GBP)',
    advertisedRent: 'Advertised Rent (GBP)',
    depositAmount: 'Security Deposit (GBP)',
    depositScheme: 'Deposit Protection Scheme',
    petPolicy: 'Pet Policy',
    petBondAmount: 'Pet Bond Amount',
    furnishingStatus: 'Furnishing Status',
    epcRating: 'EPC Rating',
    arrearsAmount: 'Arrears Amount',
    totalArrears: 'Total Arrears',
    disputeType: 'Dispute Type',
    disputeDescription: 'Dispute Description',
    courtName: 'Court Name',
    caseNumber: 'Case Number',
    hearingDate: 'Hearing Date',
    claimantName: 'Claimant Name',
    defendantName: 'Defendant Name',
    witnessName: 'Witness Name',
    mediatorName: 'Mediator Name',
    party1Name: 'Party 1 Name',
    party2Name: 'Party 2 Name',
    inspectionDate: 'Inspection Date',
    inspectorName: 'Inspector Name',
    proposedNewRent: 'Proposed New Rent (GBP)',
    rentIncreaseDate: 'Proposed Rent Increase Date',
    rentPaymentPeriod: 'Rent Payment Period',
    tenancyType: 'Current Tenancy Type',
    additionalNotes: 'Additional Notes',
  };

  const partyFields = ['landlordName', 'landlordEmail', 'landlordAddress', 'tenantName', 'tenantEmail', 'tenantCompanyName', 'claimantName', 'defendantName', 'witnessName', 'mediatorName', 'party1Name', 'party2Name', 'inspectorName', 'party1Role', 'party2Role'];
  const propertyFields = ['propertyAddress', 'propertyType', 'numBedrooms', 'numBathrooms', 'furnishingStatus', 'propertyDescription'];
  const financialFields = ['rentAmount', 'advertisedRent', 'depositAmount', 'petBondAmount', 'arrearsAmount', 'totalArrears', 'currentRent', 'newRent', 'monthlyRent', 'serviceCharge', 'financialTerms', 'disputeValue', 'financialValue', 'costEstimate', 'disputeAmountClaimed', 'latePaymentInterestRate', 'interestAmount'];

  let uploadedDocumentContent = '';

  for (const [key, value] of Object.entries(formData)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'existingDocument') {
      const docText = String(value);
      const MAX_UPLOAD_CHARS = 80000;
      uploadedDocumentContent = docText.length > MAX_UPLOAD_CHARS
        ? docText.substring(0, MAX_UPLOAD_CHARS) + '\n\n[DOCUMENT TRUNCATED - Original document exceeded maximum length]'
        : docText;
      continue;
    }
    const label = fieldLabels[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
    const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
    const line = `- ${label}: ${displayValue}`;

    if (partyFields.includes(key)) {
      grouped['Parties & Contact'].push(line);
    } else if (propertyFields.includes(key)) {
      grouped['Property Details'].push(line);
    } else if (financialFields.includes(key)) {
      grouped['Financial Terms'].push(line);
    } else if (key.includes('date') || key.includes('Date') || key.includes('term') || key.includes('Term') || key.includes('duration') || key.includes('period') || key.includes('notice')) {
      grouped['Tenancy Terms'].push(line);
    } else if (key.includes('compliance') || key.includes('Compliance') || key.includes('certificate') || key.includes('Certificate') || key.includes('safety') || key.includes('Safety') || key.includes('epc') || key.includes('eicr') || key.includes('right') || key.includes('Right') || key.includes('section') || key.includes('Section') || key.includes('ground') || key.includes('Ground') || key.includes('law') || key.includes('Law') || key.includes('abolished') || key.includes('protection') || key.includes('protocol')) {
      grouped['Legal & Compliance'].push(line);
    } else {
      grouped['Other Details'].push(line);
    }
  }

  const sections = Object.entries(grouped)
    .filter(([, lines]) => lines.length > 0)
    .map(([heading, lines]) => `${heading}:\n${lines.join('\n')}`)
    .join('\n\n');

  const multiPartInstruction = isMultiPart
    ? `\n\nIMPORTANT: This document will be written in multiple parts due to its length. For this first part, write the Table of Contents and begin the main body sections. Write as much as you can. If the document is not yet complete (i.e., you have not written the SIGNATURES/EXECUTION section), end your response with ${CONTINUATION_MARKER} on its own line. If you complete the entire document including SIGNATURES, end naturally without the marker.`
    : '';

  const uploadedDocSection = uploadedDocumentContent
    ? `\n\nUPLOADED DOCUMENT CONTENT:\n--- BEGIN UPLOADED DOCUMENT ---\n${uploadedDocumentContent}\n--- END UPLOADED DOCUMENT ---\n\nIMPORTANT: You MUST carefully read and analyse the uploaded document above. Extract all relevant information from it (names, addresses, rent amounts, dates, terms, clauses, etc.) and use this extracted information together with the form data to produce the output document. If any required information cannot be found in the uploaded document, clearly flag it as missing.`
    : '';

  return `Document: ${docTitle}
Target Length: ${pageTarget.minPages}-${pageTarget.maxPages} pages (approximately ${pageTarget.estimatedWords.toLocaleString()} words)

FORM DATA:
${sections}${uploadedDocSection}

Write a comprehensive PREMIUM VERSION of this document. Incorporate EVERY detail from the form data above${uploadedDocumentContent ? ' and the uploaded document content' : ''}. The document must be ${pageTarget.minPages}-${pageTarget.maxPages} pages with exhaustive legal documentation, extensive schedules and appendices, comprehensive procedures, and complete legal framework. Do not fall short of the target length.${multiPartInstruction}`;
};

const callStreamingEdgeFunction = async (
  systemPrompt: string,
  userPrompt: string,
  onChunk: (text: string) => void,
  options?: {
    maxTokens?: number;
    continuationContext?: string;
    partIndex?: number;
  },
): Promise<StreamResult> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

  try {
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/generate-document`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          action: 'generate',
          systemPrompt,
          userPrompt,
          maxTokens: options?.maxTokens ?? 64000,
          temperature: 0.2,
          stream: true,
          continuationContext: options?.continuationContext,
          partIndex: options?.partIndex,
        }),
        signal: controller.signal,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Edge function failed with status ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body stream available');
    }

    const decoder = new TextDecoder();
    let accumulated = '';
    let buffer = '';
    let stopReason: string | undefined;

    while (true) {
      const readPromise = reader.read();
      const stallPromise = new Promise<never>((_, reject) => {
        const id = setTimeout(() => {
          reject(new Error('Stream stalled: no data received'));
        }, STREAM_STALL_MS);
        readPromise.then(() => clearTimeout(id), () => clearTimeout(id));
      });
      const { done, value } = await Promise.race([readPromise, stallPromise]);
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;

        const jsonStr = trimmed.slice(6);
        try {
          const parsed = JSON.parse(jsonStr);

          if (parsed.error) {
            throw new Error(parsed.error);
          }

          if (parsed.done) {
            stopReason = parsed.stop_reason;
            return { content: accumulated, stopReason };
          }

          if (parsed.delta) {
            accumulated += parsed.delta;
            onChunk(parsed.delta);
          }
        } catch (parseErr) {
          if (parseErr instanceof SyntaxError) continue;
          throw parseErr;
        }
      }
    }

    return { content: accumulated, stopReason };
  } finally {
    clearTimeout(timeout);
  }
};

const generateSinglePart = async ({
  documentType,
  formData,
  config,
  onProgress,
  onStreamChunk
}: {
  documentType: string;
  formData: Record<string, any>;
  config: any;
  onProgress?: (progress: GenerationProgress) => void;
  onStreamChunk?: (accumulated: string, chunkLength: number) => void;
}): Promise<string> => {
  const systemPrompt = config.prompts['premium'];
  const userPrompt = buildUserPrompt(documentType, formData, false);
  const estimatedTotal = getEstimatedTotalChars(documentType);
  let totalStreamed = 0;

  onProgress?.({
    status: 'generating',
    message: 'Writing agreement...',
    streamedChars: 0,
    estimatedTotalChars: estimatedTotal,
    currentPart: 1,
    totalParts: 1,
  });

  const result = await callStreamingEdgeFunction(
    systemPrompt,
    userPrompt,
    (chunk: string) => {
      totalStreamed += chunk.length;
      onProgress?.({
        status: 'streaming',
        message: 'Writing agreement...',
        streamedChars: totalStreamed,
        estimatedTotalChars: estimatedTotal,
        currentPart: 1,
        totalParts: 1,
      });
      onStreamChunk?.(chunk, totalStreamed);
    }
  );

  onProgress?.({
    status: 'complete',
    message: 'Agreement written successfully!',
    streamedChars: result.content.length,
    estimatedTotalChars: estimatedTotal,
    currentPart: 1,
    totalParts: 1,
  });

  return result.content;
};

const generateMultiPart = async ({
  documentType,
  formData,
  config,
  onProgress,
  onStreamChunk,
}: {
  documentType: string;
  formData: Record<string, any>;
  config: any;
  onProgress?: (progress: GenerationProgress) => void;
  onStreamChunk?: (accumulated: string, chunkLength: number) => void;
}): Promise<string> => {
  const systemPrompt = config.prompts['premium'];
  const userPrompt = buildUserPrompt(documentType, formData, true);
  const estimatedTotal = getEstimatedTotalChars(documentType);
  const estimatedParts = getEstimatedParts(documentType);
  const tokensPerPart = getMaxTokensPerPart();

  let accumulatedContent = '';
  let totalStreamed = 0;
  let currentPart = 0;

  for (let partIndex = 0; partIndex < MAX_PARTS; partIndex++) {
    currentPart = partIndex + 1;
    const isFirstPart = partIndex === 0;

    const partMessage = currentPart <= estimatedParts
      ? `Writing part ${currentPart} of ~${estimatedParts}...`
      : `Writing additional section ${currentPart}...`;

    onProgress?.({
      status: isFirstPart ? 'generating' : 'streaming',
      message: partMessage,
      streamedChars: totalStreamed,
      estimatedTotalChars: estimatedTotal,
      currentPart,
      totalParts: estimatedParts,
    });

    const result = await callStreamingEdgeFunction(
      systemPrompt,
      userPrompt,
      (chunk: string) => {
        totalStreamed += chunk.length;
        onProgress?.({
          status: 'streaming',
          message: partMessage,
          streamedChars: totalStreamed,
          estimatedTotalChars: estimatedTotal,
          currentPart,
          totalParts: estimatedParts,
        });
        onStreamChunk?.(chunk, totalStreamed);
      },
      {
        maxTokens: tokensPerPart,
        continuationContext: isFirstPart ? undefined : accumulatedContent.slice(-CONTEXT_TAIL_CHARS),
        partIndex,
      }
    );

    accumulatedContent += result.content;

    const contentEndsWithContinue = result.content.trim().endsWith(CONTINUATION_MARKER);
    const stoppedDueToMaxTokens = result.stopReason === 'max_tokens';
    const needsContinuation = contentEndsWithContinue || stoppedDueToMaxTokens;

    if (!needsContinuation) {
      break;
    }

    if (contentEndsWithContinue) {
      accumulatedContent = accumulatedContent.slice(0, accumulatedContent.lastIndexOf(CONTINUATION_MARKER));
    }
  }

  onProgress?.({
    status: 'complete',
    message: 'Agreement written successfully!',
    streamedChars: accumulatedContent.length,
    estimatedTotalChars: estimatedTotal,
    currentPart,
    totalParts: estimatedParts,
  });

  return accumulatedContent;
};

function stripContinuationMarkers(content: string): string {
  return content.replace(/\[CONTINUE\]/g, '').trimEnd();
}

function hasSignatureSection(content: string): boolean {
  const signaturePatterns = [
    /SIGNATURE/i,
    /EXECUTION/i,
    /Signed\s*:/i,
    /_{3,}\s*\n.*(?:Landlord|Tenant|Party|Witness)/i,
    /IN WITNESS WHEREOF/i,
  ];
  const lastQuarter = content.slice(Math.floor(content.length * 0.6));
  return signaturePatterns.some(pattern => pattern.test(lastQuarter));
}

function ensureDocumentCompleteness(content: string): string {
  if (hasSignatureSection(content)) {
    return content;
  }

  const signatureBlock = `

====================================================================
SIGNATURES AND EXECUTION
====================================================================

IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the date first written above.

LANDLORD:

Signature: _________________________________

Print Name: ________________________________

Date: ______________________________________


TENANT:

Signature: _________________________________

Print Name: ________________________________

Date: ______________________________________


WITNESS (for Landlord):

Signature: _________________________________

Print Name: ________________________________

Address: ___________________________________

Date: ______________________________________


WITNESS (for Tenant):

Signature: _________________________________

Print Name: ________________________________

Address: ___________________________________

Date: ______________________________________
`;

  return content + signatureBlock;
}

export const generateAllVersions = async ({
  documentType,
  formData
}: {
  documentType: string;
  formData: Record<string, any>;
}) => {
  const [standard, enhanced, premium] = await Promise.all([
    generateDocument({ documentType, formData, tier: 'standard' }),
    generateDocument({ documentType, formData, tier: 'enhanced' }),
    generateDocument({ documentType, formData, tier: 'premium' }),
  ]);

  return {
    standard: standard.content,
    enhanced: enhanced.content,
    premium: premium.content,
    standardTime: standard.generationTimeMs,
    enhancedTime: enhanced.generationTimeMs,
    premiumTime: premium.generationTimeMs,
    standardCacheHit: standard.cacheHit,
    enhancedCacheHit: enhanced.cacheHit,
    premiumCacheHit: premium.cacheHit,
  };
};
